import {
	StreamService,
	GameService,
	StretchLiveApiService
} from "@prestosports/prestostats-core/Services";
import { SportCodes } from "@prestosports/prestostats-core/Common";
import {
	SAVE_STREAM,
	SAVE_STREAM_COMPLETE,
	DELETE_STREAM_MARKER,
	DELETE_STREAM_MARKER_COMPLETE,
	UPDATE_STREAM_MARKER,
	UPDATE_STREAM_MARKER_COMPLETE,
	DELETE_STREAM,
	DELETE_STREAM_COMPLETE,
	GET_STREAMS,
	GET_STREAMS_COMPLETE,
	INIT_STREAM_DATA,
	INIT_STREAM_DATA_COMPLETE,
	GET_PROVIDER_SRC,
	GET_PROVIDER_SRC_COMPLETE,
	TOGGLE_STREAM_OPEN,
	SET_TIME_SKIP,
	RESET_TIME_SKIP,
	SET_POPOUT_CLOSED,
	SET_POPOUT_OPEN,
	SET_STREAM_EVENT_ID
} from "./constants";
import { getGameAccess, withQueuedSync, withSync } from "../app/actions";
import { CommonRoutes } from "../../routes/common-routes";
import {
	overWriteEventData,
	selectEventStream,
	setCurrentEvent
} from "../events/actions";
import StretchliveApiClient from "../../services/stretchlive-api-client";
import { isDefined } from "../../util/object-util";
import { initPlaysData } from "../football-play/actions";

StretchLiveApiService.setStretchLiveApiClient(StretchliveApiClient);

export const saveStreamMarker = async (eventId, streamId, marker, dispatch) => {
	withQueuedSync(
		eventId,
		async () => {
			dispatch({
				type: UPDATE_STREAM_MARKER
			});

			const saved = await StreamService.saveStreamMarker(streamId, marker);

			dispatch({
				type: UPDATE_STREAM_MARKER_COMPLETE,
				payload: { stream: saved, eventId }
			});
		},
		dispatch
	);
};

export const deleteStreamMarker = async (
	eventId,
	streamId,
	markerId,
	dispatch
) => {
	withQueuedSync(
		eventId,
		async () => {
			dispatch({
				type: DELETE_STREAM_MARKER
			});

			const saved = await StreamService.deleteStreamMarker(streamId, markerId);

			dispatch({
				type: DELETE_STREAM_MARKER_COMPLETE,
				payload: { stream: saved, eventId }
			});
		},
		dispatch
	);
};

export const saveStream = async (eventId, stream, dispatch) => {
	withQueuedSync(
		eventId,
		async () => {
			dispatch({
				type: SAVE_STREAM
			});

			const saved = await StreamService.saveStream(stream);

			dispatch({
				type: SAVE_STREAM_COMPLETE,
				payload: { stream: saved, eventId }
			});
		},
		dispatch
	);
};

export const deleteStream = async (eventId, streamId, dispatch) => {
	withQueuedSync(
		eventId,
		async () => {
			dispatch({ type: DELETE_STREAM });

			await StreamService.deleteStream(streamId);

			dispatch({ type: DELETE_STREAM_COMPLETE, payload: { streamId, eventId } });
		},
		dispatch
	);
};

export const getStreams = async (eventId, dispatch) => {
	dispatch({ type: GET_STREAMS });

	const streams = await StreamService.getStreams(eventId);

	dispatch({ type: GET_STREAMS_COMPLETE, payload: { streams, eventId } });
};

const initSportMap = {
	[SportCodes.FlagFootball]: (game, dispatch) => initPlaysData(game, dispatch),
	[SportCodes.Football]: (game, dispatch) => initPlaysData(game, dispatch)
};

export const loadStreamData = async (eventId, dispatch) => {
	dispatch({
		type: INIT_STREAM_DATA
	});

	const { game } = await GameService.getOrCreateGame(eventId);
	const currentEvent = await setCurrentEvent(eventId, dispatch);
	await getGameAccess(game.id, dispatch);
	await getStreams(currentEvent.id, dispatch);
	await initSportMap[currentEvent.sportId]?.(game, dispatch);

	dispatch({
		type: INIT_STREAM_DATA_COMPLETE
	});
};

export const initStreamData = async (eventId, redirect, dispatch) => {
	if (!eventId) {
		redirect(CommonRoutes.NOT_FOUND.url);
	} else {
		return withSync(
			eventId,
			async () => {
				await loadStreamData(eventId, dispatch);
			},
			async () => {
				await overWriteEventData(eventId, dispatch);
			},
			dispatch
		);
	}

	return null;
};

export const getProviderSrc = async (stream, dispatch) => {
	dispatch({ type: GET_PROVIDER_SRC });

	const updatedStream = await StreamService.getProviderSrc(stream);
	if (updatedStream) {
		dispatch({
			type: GET_PROVIDER_SRC_COMPLETE,
			payload: {
				updatedStream
			}
		});
	}
};

export const toggleStreamOpen = (open, eventId, dispatch) => {
	dispatch({
		type: TOGGLE_STREAM_OPEN,
		payload: {
			open,
			eventId
		}
	});
};

export const setStreamEventId = (eventId, dispatch) => {
	dispatch({
		type: SET_STREAM_EVENT_ID,
		payload: {
			eventId
		}
	});
};

export const setTimeSkip = async (
	seconds,
	streamId,
	currentEvent,
	popoutOpen,
	dispatch
) => {
	const selectedStreamId = streamId || currentEvent.selectedStreamId;
	if (selectedStreamId !== currentEvent.selectedStreamId) {
		await selectEventStream(currentEvent.id, streamId, dispatch);
	}
	if (isDefined(seconds) && !popoutOpen) {
		toggleStreamOpen(true, currentEvent.id, dispatch);
	}
	dispatch({
		type: SET_TIME_SKIP,
		payload: {
			timeSkip: seconds,
			eventId: currentEvent.id
		}
	});
};

export const resetTimeSkip = async (eventId, dispatch) => {
	dispatch({
		type: RESET_TIME_SKIP,
		payload: {
			timeSkip: null
		}
	});
};

export const setPopoutOpen = (eventId, dispatch) => {
	dispatch({
		type: SET_POPOUT_OPEN,
		payload: {
			popoutOpen: true,
			eventId
		}
	});
};

export const setPopoutClosed = (eventId, dispatch) => {
	dispatch({
		type: SET_POPOUT_CLOSED,
		payload: {
			popoutOpen: false,
			eventId
		}
	});
};
